// src/components/AdminDashboard/AdminDashboard.js
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../../utils/api';
import courtService from '../../../services/courtService';
import { Snackbar, Alert, Tabs, Tab, Box, Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import qs from 'qs';

// Import sub-components
import DiscountCodesSection from './sections/DiscountCodesSection';
import UserRolesSection from './sections/UserRolesSection';
import AdminUserManagement from './sections/AdminUserManagement';
import AddCourtContainer from './sections/AddCourtContainer';
import CourtOwnershipSection from './sections/CourtOwnershipSection';
import CourtCoOwnerSection from './sections/CourtCoOwnerSection';
import PurchasesAfterInfluencerSection from './sections/PurchasesAfterInfluencerSection';
import UserPurchasesSection from './sections/UserPurchasesSection';
import RevenueRecordsExportSection from './sections/RevenueRecordsExportSection';
import FeedbackManagementSection from './sections/FeedbackManagementSection';
import UserFootageSection from './sections/UserFootageSection';
import ReportingSection from './sections/ReportingSection';
import AdminSubscriptionManagement from '../../subscription/AdminSubscriptionManagement';
import SubscriptionFeatureConfig from '../../subscription/SubscriptionFeatureConfig';
import NotificationTestPanel from '../../notifications/NotificationTestPanel';

// Organized admin dashboard sections with logical groupings
const ADMIN_SECTIONS = {
  // 📊 Analytics & Reports
  analytics: {
    title: 'Analytics & Reports',
    icon: '📊',
    sections: [
      { label: 'User Purchases', value: 'purchases', description: 'View and manage user purchase data' },
      { label: 'Revenue Records', value: 'revenue', description: 'Export and analyze revenue data' },
      { label: 'Reports', value: 'reports', description: 'Daily signup and performance reports' },
    ]
  },
  
  // 👥 User Management
  users: {
    title: 'User Management',
    icon: '👥',
    sections: [
      { label: 'User Management', value: 'userManagement', description: 'Comprehensive user administration' },
      { label: 'User Roles', value: 'roles', description: 'Assign and manage user roles' },
      { label: 'Feedback', value: 'feedback', description: 'Review and manage user feedback' },
      { label: 'User Footage', value: 'footage', description: 'Manage user-generated content' },
    ]
  },
  
  // 🏢 Business Operations
  business: {
    title: 'Business Operations',
    icon: '🏢',
    sections: [
      { label: 'Discount Codes', value: 'discounts', description: 'Create and manage discount codes' },
      { label: 'Courts', value: 'courts', description: 'Manage court locations and ownership' },
    ]
  },
  
  // ⚙️ System Configuration
  system: {
    title: 'System Configuration',
    icon: '⚙️',
    sections: [
      { label: 'Subscription Management', value: 'subscription', description: 'Manage subscription plans and features' },
      { label: 'Feature Config', value: 'featureConfig', description: 'Configure system features and flags' },
      { label: 'Notification Tests', value: 'notifications', description: 'Test notification systems' },
    ]
  }
};

const AdminDashboard = () => {
  // ────────────── STATE VARIABLES ──────────────
  const [discountCodes, setDiscountCodes] = useState([]);
  const [loadingDiscountCodes, setLoadingDiscountCodes] = useState(true);
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState('');

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [loadingAssign, setLoadingAssign] = useState(false);
  const [assignSuccess, setAssignSuccess] = useState('');
  const [assignError, setAssignError] = useState('');

  const [courts, setCourts] = useState([]);
  const [selectedCourt, setSelectedCourt] = useState('');
  const [selectedCourtOwner, setSelectedCourtOwner] = useState('');
  const [loadingAssignCourt, setLoadingAssignCourt] = useState(false);
  const [assignCourtSuccess, setAssignCourtSuccess] = useState('');
  const [assignCourtError, setAssignCourtError] = useState('');

  // Co-owner state
  const [selectedCoOwnerCourt, setSelectedCoOwnerCourt] = useState('');
  const [selectedCoOwner, setSelectedCoOwner] = useState('');
  const [loadingAddCoOwner, setLoadingAddCoOwner] = useState(false);
  const [addCoOwnerSuccess, setAddCoOwnerSuccess] = useState('');
  const [addCoOwnerError, setAddCoOwnerError] = useState('');

  const [purchases, setPurchases] = useState([]);
  const [loadingPurchases, setLoadingPurchases] = useState(true);
  const [purchaseError, setPurchaseError] = useState('');
  const [purchasePage, setPurchasePage] = useState(0);
  const [purchaseTotalPages, setPurchaseTotalPages] = useState(0);
  const [purchaseTotalElements, setPurchaseTotalElements] = useState(0);
  const [purchaseSize, setPurchaseSize] = useState(15);
  const [purchaseSearchTerm, setPurchaseSearchTerm] = useState('');
  const [purchaseFilterCourt, setPurchaseFilterCourt] = useState('');
  const [purchaseFilterDateRange, setPurchaseFilterDateRange] = useState('all');
  const availablePurchaseCourts = useMemo(() => {
    const names = (courts || [])
      .map((c) => c?.courtName || c?.name)
      .filter(Boolean);
    return [...new Set(names)].sort();
  }, [courts]);

  const [feedbackList, setFeedbackList] = useState([]);
  const [loadingFeedback, setLoadingFeedback] = useState(true);
  const [feedbackError, setFeedbackError] = useState('');
  const [deleteFeedbackSuccess, setDeleteFeedbackSuccess] = useState('');

  const [selectedUserForPurchases, setSelectedUserForPurchases] = useState('');
  const [filteredPurchases, setFilteredPurchases] = useState([]);
  const [loadingFilteredPurchases, setLoadingFilteredPurchases] = useState(false);
  const [purchaseQueryError, setPurchaseQueryError] = useState('');

  const [exportError, setExportError] = useState('');
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Tab state - initialize to first tab of first section
  const [tab, setTab] = useState('purchases');
  const handleTabChange = (event, newValue) => setTab(newValue);
  
  // Helper function to check if a tab value is valid for the current section
  const isValidTabForSection = (tabValue, sectionKey) => {
    const section = ADMIN_SECTIONS[sectionKey];
    return section.sections.some(s => s.value === tabValue);
  };

  // ────────────── DATA FETCHING ──────────────
  useEffect(() => {
    const fetchDiscountCodes = async () => {
      try {
        const response = await api.get('/admin/discount-codes/all');
        setDiscountCodes(response.data);
      } catch (error) {
        setDeleteError('Failed to fetch discount codes.');
      } finally {
        setLoadingDiscountCodes(false);
      }
    };
    fetchDiscountCodes();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get('/users/admin');
        setUsers(response.data.content || []);
      } catch (error) {
        setAssignError('Failed to fetch users.');
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchAllCourts = async () => {
      try {
        const courts = await courtService.getAllCourts();
        setCourts(courts);
      } catch (error) {
      }
    };
    fetchAllCourts();
  }, []);

  useEffect(() => {
    const fetchUserPurchases = async () => {
      setLoadingPurchases(true);
      setPurchaseError('');
      try {
        const res = await api.get('/users/admin/video-purchases', {
          params: {
            page: purchasePage,
            size: purchaseSize,
            sort: 'purchaseDate,desc', // Sort by newest first
            search: purchaseSearchTerm || undefined,
            courtName: purchaseFilterCourt || undefined,
            dateRange: purchaseFilterDateRange !== 'all' ? purchaseFilterDateRange : undefined
          }
        });
        
        // Handle paginated response
        if (res.data.content) {
          setPurchases(res.data.content || []);
          setPurchaseTotalPages(res.data.totalPages || 0);
          setPurchaseTotalElements(res.data.totalElements || 0);
        } else {
          setPurchaseError('Failed to fetch user purchases.');
        }
      } catch (err) {
        setPurchaseError(err.response?.data?.message || 'Failed to fetch user purchases.');
        setPurchases([]);
        setPurchaseTotalPages(0);
        setPurchaseTotalElements(0);
      } finally {
        setLoadingPurchases(false);
      }
    };
    fetchUserPurchases();
  }, [purchasePage, purchaseSize, purchaseSearchTerm, purchaseFilterCourt, purchaseFilterDateRange]);

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        const response = await api.get('/feedback/all');
        setFeedbackList(response.data);
      } catch (error) {
        setFeedbackError('Failed to fetch feedback.');
      } finally {
        setLoadingFeedback(false);
      }
    };
    fetchFeedback();
  }, []);

  useEffect(() => {
    const fetchFilteredPurchases = async () => {
      if (!selectedUserForPurchases) return;
      setLoadingFilteredPurchases(true);
      try {
        const response = await api.get(`/users/admin/${selectedUserForPurchases}/purchases/influencer-latest`);
        setFilteredPurchases(response.data);
      } catch (error) {
        setPurchaseQueryError('Failed to fetch purchases for selected user.');
      } finally {
        setLoadingFilteredPurchases(false);
      }
    };
    fetchFilteredPurchases();
  }, [selectedUserForPurchases]);

  // ────────────── EVENT HANDLERS ──────────────
  const handleAssignRole = async (e) => {
    e.preventDefault();
    if (!selectedUser || !selectedRole) {
      setAssignError('Please select both a user and a role.');
      return;
    }
    try {
      setLoadingAssign(true);
      // Find user ID by username first
      const user = users.find(u => u.username === selectedUser);
      if (!user) {
        throw new Error('User not found');
      }
      
      await api.post(`/users/admin/${user.id}/roles/${selectedRole.toUpperCase()}`);
      setAssignSuccess(`Role "${selectedRole}" assigned to "${selectedUser}" successfully.`);
      const response = await api.get('/users/admin');
      setUsers(response.data.content || []);
      setSelectedUser('');
      setSelectedRole('');
    } catch (error) {
      setAssignError(error.response?.data?.message || 'Failed to assign role.');
    } finally {
      setLoadingAssign(false);
    }
  };

  const handleAssignCourtOwnership = async (e) => {
    e.preventDefault();
    if (!selectedCourt || !selectedCourtOwner) {
      setAssignCourtError('Please select both a court and a user.');
      return;
    }
    try {
      setLoadingAssignCourt(true);
      await courtService.assignCourtOwnership(selectedCourt, selectedCourtOwner);
      setAssignCourtSuccess(`Court ownership assigned to ${selectedCourtOwner}.`);
    } catch (error) {
      setAssignCourtError(error.response?.data?.message || 'Failed to assign court ownership.');
    } finally {
      setLoadingAssignCourt(false);
      setSelectedCourt('');
      setSelectedCourtOwner('');
    }
  };

  const handleAddCourtCoOwner = async (e) => {
    e.preventDefault();
    if (!selectedCoOwnerCourt || !selectedCoOwner) {
      setAddCoOwnerError('Please select both a court and a user.');
      return;
    }
    try {
      setLoadingAddCoOwner(true);
      await courtService.addCourtCoOwner(selectedCoOwnerCourt, selectedCoOwner);
      setAddCoOwnerSuccess(`Co-owner ${selectedCoOwner} added to court successfully.`);
    } catch (error) {
      setAddCoOwnerError(error.response?.data?.message || 'Failed to add co-owner.');
    } finally {
      setLoadingAddCoOwner(false);
      setSelectedCoOwnerCourt('');
      setSelectedCoOwner('');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/discount-codes/${id}`);
      setDiscountCodes((prev) => prev.filter((code) => code.id !== id));
      setDeleteSuccess('Discount code deleted successfully.');
    } catch (error) {
      setDeleteError('Failed to delete discount code.');
    }
  };

  const handleDeleteFeedback = async (feedbackId) => {
    try {
      await api.delete(`/feedback/delete/${feedbackId}`);
      setFeedbackList((prev) => prev.filter((fb) => fb.id !== feedbackId));
      setDeleteFeedbackSuccess('Feedback deleted successfully.');
    } catch (error) {
      setFeedbackError('Failed to delete feedback.');
    }
  };

  const handleMonthChange = (event) => {
    setSelectedMonths(event.target.value);
  };

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  const handleExportRevenue = async () => {
    try {
      setExportError('');
      const response = await api.get('/users/admin/purchases/export', {
        responseType: 'arraybuffer',
        params: { months: selectedMonths,
          year: selectedYear
         },
         paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' })
      });
      const blob = new Blob([response.data], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'purchases.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setExportError('Failed to export revenue records.');
    }
  };

  // ────────────── RENDER ──────────────
  return (
<div className="bg-gradient-to-br from-primary-bg to-gray-50 
  p-4 sm:p-6 md:p-10 
  max-w-7xl mx-auto my-6 sm:my-8 md:my-10 
  rounded-3xl shadow-2xl relative overflow-hidden 
  before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 
  before:h-1 before:bg-gradient-to-r before:from-accent-teal before:to-accent-orange">

      {/* Title */}
      <Typography 
  variant="h4" 
  className="text-accent-grey mb-6 sm:mb-8 text-center font-bold 
             text-2xl sm:text-3xl md:text-4xl 
             bg-gradient-to-r from-accent-teal to-accent-grey bg-clip-text text-transparent drop-shadow-sm"
>

        Admin Dashboard
      </Typography>
      
      {/* Organized Navigation with Grouped Sections */}
      <div className="bg-white rounded-2xl shadow-admin-card mb-8 overflow-hidden border border-accent-teal/10">
        {Object.entries(ADMIN_SECTIONS).map(([groupKey, group]) => (
          <Accordion key={groupKey} defaultExpanded={groupKey === 'analytics'}>
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              className="bg-accent-teal/5 hover:bg-accent-teal/10 transition-colors duration-300"
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="h6" className="text-xl">
                  {group.icon}
                </Typography>
                <Typography variant="h6" className="font-bold text-accent-grey">
                  {group.title}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className="p-0">
              <Tabs
                value={isValidTabForSection(tab, groupKey) ? tab : group.sections[0].value}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                className="[&_.MuiTabs-flexContainer]:flex-wrap 
             [&_.MuiTabs-flexContainer]:gap-2 
             [&_.MuiTabs-flexContainer]:p-2 sm:[&_.MuiTabs-flexContainer]:p-4 
             [&_.MuiTab-root]:min-w-auto [&_.MuiTab-root]:px-2 [&_.MuiTab-root]:py-1 
             sm:[&_.MuiTab-root]:p-2 
             [&_.MuiTab-root]:mx-1 [&_.MuiTab-root]:rounded-lg 
             [&_.MuiTab-root]:bg-accent-teal/5 
             [&_.MuiTab-root]:border [&_.MuiTab-root]:border-accent-teal/10 
             [&_.MuiTab-root]:transition-all [&_.MuiTab-root]:duration-300 
             [&_.MuiTab-root]:hover:bg-accent-teal/10 
             [&_.MuiTab-root]:hover:border-accent-teal/30 
             [&_.MuiTab-root]:hover:-translate-y-0.5 
             [&_.MuiTab-root]:Mui-selected:bg-accent-teal/15 
             [&_.MuiTab-root]:Mui-selected:border-accent-teal/40 
             [&_.MuiTab-root]:Mui-selected:font-bold 
             [&_.MuiTabs-indicator]:hidden"
              >
                {group.sections.map((section) => (
                  <Tab 
  key={section.value} 
  wrapped
  label={
    <Box className="text-left">
      <Typography variant="body2" className="font-inherit">
        {section.label}
      </Typography>
      <Typography 
        variant="caption" 
        className="text-gray-500 text-[0.7rem] sm:text-xs block mt-0.5"
      >
        {section.description}
      </Typography>
    </Box>
  } 
  value={section.value}
/>

                ))}
              </Tabs>
            </AccordionDetails>
          </Accordion>
        ))}
      </div>
      {/* Organized Content Sections */}
      
      {/* 📊 Analytics & Reports */}
      {(tab === 'purchases' || tab === 'revenue' || tab === 'reports') && (
        <div className="mb-10 last:mb-0">
          <Typography variant="h5" className="text-accent-grey font-bold text-xl mb-4 pl-4 border-l-4 border-accent-teal">
            📊 Analytics & Reports
          </Typography>
          
          {tab === 'purchases' && (
            <>
              <PurchasesAfterInfluencerSection
                users={users}
                selectedUserForPurchases={selectedUserForPurchases}
                setSelectedUserForPurchases={setSelectedUserForPurchases}
                filteredPurchases={filteredPurchases}
                loadingFilteredPurchases={loadingFilteredPurchases}
                purchaseQueryError={purchaseQueryError}
              />
              <UserPurchasesSection
                purchases={purchases}
                loadingPurchases={loadingPurchases}
                purchaseError={purchaseError}
                currentPage={purchasePage + 1}
                totalPages={purchaseTotalPages}
                totalElements={purchaseTotalElements}
                pageSize={purchaseSize}
                onPageChange={(page) => setPurchasePage(page - 1)}
                onPageSizeChange={(size) => {
                  setPurchasePage(0);
                  setPurchaseSize(size);
                }}
                searchTerm={purchaseSearchTerm}
                onSearchTermChange={(value) => {
                  setPurchasePage(0);
                  setPurchaseSearchTerm(value);
                }}
                filterCourt={purchaseFilterCourt}
                onFilterCourtChange={(value) => {
                  setPurchasePage(0);
                  setPurchaseFilterCourt(value);
                }}
                filterDateRange={purchaseFilterDateRange}
                onFilterDateRangeChange={(value) => {
                  setPurchasePage(0);
                  setPurchaseFilterDateRange(value);
                }}
                availableCourts={availablePurchaseCourts}
                onClearFilters={() => {
                  setPurchasePage(0);
                  setPurchaseSearchTerm('');
                  setPurchaseFilterCourt('');
                  setPurchaseFilterDateRange('all');
                }}
              />
            </>
          )}
          
          {tab === 'revenue' && (
            <RevenueRecordsExportSection
              selectedMonths={selectedMonths}
              selectedYear={selectedYear}
              handleYearChange={handleYearChange}
              handleMonthChange={handleMonthChange}
              handleExportRevenue={handleExportRevenue}
              exportError={exportError}
            />
          )}
          
          {tab === 'reports' && (
            <ReportingSection />
          )}
        </div>
      )}

      {/* 👥 User Management */}
      {(tab === 'userManagement' || tab === 'roles' || tab === 'feedback' || tab === 'footage') && (
        <div className="mb-10 last:mb-0">
          <Typography variant="h5" className="text-accent-grey font-bold text-xl mb-4 pl-4 border-l-4 border-accent-teal">
            👥 User Management
          </Typography>
          
          {tab === 'userManagement' && (
            <AdminUserManagement />
          )}
          
          {tab === 'roles' && (
            <UserRolesSection
              users={users}
              loadingUsers={loadingUsers}
              selectedUser={selectedUser}
              selectedRole={selectedRole}
              setSelectedUser={setSelectedUser}
              setSelectedRole={setSelectedRole}
              handleAssignRole={handleAssignRole}
              loadingAssign={loadingAssign}
              assignError={assignError}
              assignSuccess={assignSuccess}
            />
          )}
          
          {tab === 'feedback' && (
            <FeedbackManagementSection
              feedbackList={feedbackList}
              loadingFeedback={loadingFeedback}
              feedbackError={feedbackError}
              handleDeleteFeedback={handleDeleteFeedback}
            />
          )}
          
          {tab === 'footage' && (
            <UserFootageSection />
          )}
        </div>
      )}

      {/* 🏢 Business Operations */}
      {(tab === 'discounts' || tab === 'courts') && (
        <div className="mb-10 last:mb-0">
          <Typography variant="h5" className="text-accent-grey font-bold text-xl mb-4 pl-4 border-l-4 border-accent-teal">
            🏢 Business Operations
          </Typography>
          
          {tab === 'discounts' && (
            <DiscountCodesSection
              discountCodes={discountCodes}
              loadingDiscountCodes={loadingDiscountCodes}
              deleteError={deleteError}
              handleDelete={handleDelete}
            />
          )}
          
          {tab === 'courts' && (
            <>
              <AddCourtContainer />
              <CourtOwnershipSection
                courts={courts}
                users={users}
                loadingUsers={loadingUsers}
                selectedCourt={selectedCourt}
                setSelectedCourt={setSelectedCourt}
                selectedCourtOwner={selectedCourtOwner}
                setSelectedCourtOwner={setSelectedCourtOwner}
                handleAssignCourtOwnership={handleAssignCourtOwnership}
                loadingAssignCourt={loadingAssignCourt}
              />
              <CourtCoOwnerSection
                courts={courts}
                users={users}
                loadingUsers={loadingUsers}
                selectedCourt={selectedCoOwnerCourt}
                setSelectedCourt={setSelectedCoOwnerCourt}
                selectedCoOwner={selectedCoOwner}
                setSelectedCoOwner={setSelectedCoOwner}
                handleAddCourtCoOwner={handleAddCourtCoOwner}
                loadingAddCoOwner={loadingAddCoOwner}
              />
            </>
          )}
        </div>
      )}

      {/* ⚙️ System Configuration */}
      {(tab === 'subscription' || tab === 'featureConfig' || tab === 'notifications') && (
        <div className="mb-10 last:mb-0">
          <Typography variant="h5" className="text-accent-grey font-bold text-xl mb-4 pl-4 border-l-4 border-accent-teal">
            ⚙️ System Configuration
          </Typography>
          
          {tab === 'subscription' && (
            <AdminSubscriptionManagement />
          )}
          
          {tab === 'featureConfig' && (
            <SubscriptionFeatureConfig />
          )}
          
          {tab === 'notifications' && (
            <NotificationTestPanel />
          )}
        </div>
      )}
      
      <Snackbar open={!!deleteSuccess} autoHideDuration={6000} onClose={() => setDeleteSuccess('')}>
        <Alert onClose={() => setDeleteSuccess('')} severity="success" sx={{ width: '100%' }}>
          {deleteSuccess}
        </Alert>
      </Snackbar>
      <Snackbar open={!!deleteError} autoHideDuration={6000} onClose={() => setDeleteError('')}>
        <Alert onClose={() => setDeleteError('')} severity="error" sx={{ width: '100%' }}>
          {deleteError}
        </Alert>
      </Snackbar>
      <Snackbar open={!!assignSuccess} autoHideDuration={6000} onClose={() => setAssignSuccess('')}>
        <Alert onClose={() => setAssignSuccess('')} severity="success" sx={{ width: '100%' }}>
          {assignSuccess}
        </Alert>
      </Snackbar>
      <Snackbar open={!!assignError} autoHideDuration={6000} onClose={() => setAssignError('')}>
        <Alert onClose={() => setAssignError('')} severity="error" sx={{ width: '100%' }}>
          {assignError}
        </Alert>
      </Snackbar>
      <Snackbar open={!!deleteFeedbackSuccess} autoHideDuration={6000} onClose={() => setDeleteFeedbackSuccess('')}>
        <Alert onClose={() => setDeleteFeedbackSuccess('')} severity="success" sx={{ width: '100%' }}>
          {deleteFeedbackSuccess}
        </Alert>
      </Snackbar>
      <Snackbar open={!!feedbackError} autoHideDuration={6000} onClose={() => setFeedbackError('')}>
        <Alert onClose={() => setFeedbackError('')} severity="error" sx={{ width: '100%' }}>
          {feedbackError}
        </Alert>
      </Snackbar>
      <Snackbar open={!!assignCourtSuccess} autoHideDuration={6000} onClose={() => setAssignCourtSuccess('')}>
        <Alert onClose={() => setAssignCourtSuccess('')} severity="success" sx={{ width: '100%' }}>
          {assignCourtSuccess}
        </Alert>
      </Snackbar>
      <Snackbar open={!!assignCourtError} autoHideDuration={6000} onClose={() => setAssignCourtError('')}>
        <Alert onClose={() => setAssignCourtError('')} severity="error" sx={{ width: '100%' }}>
          {assignCourtError}
        </Alert>
      </Snackbar>
      <Snackbar open={!!addCoOwnerSuccess} autoHideDuration={6000} onClose={() => setAddCoOwnerSuccess('')}>
        <Alert onClose={() => setAddCoOwnerSuccess('')} severity="success" sx={{ width: '100%' }}>
          {addCoOwnerSuccess}
        </Alert>
      </Snackbar>
      <Snackbar open={!!addCoOwnerError} autoHideDuration={6000} onClose={() => setAddCoOwnerError('')}>
        <Alert onClose={() => setAddCoOwnerError('')} severity="error" sx={{ width: '100%' }}>
          {addCoOwnerError}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AdminDashboard;
